import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Layers, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { AUTO_RESOLVE_BATCH_ENABLED } from '../../config/featureFlags';
import { AUTO_RESOLVE_BATCH_ACTIONS } from '../../config/autoResolve';
import BatchCard, { BatchStatusChip, DismissButton, FoldReasonChip } from './AutoResolveBatchCard';
import {
  BATCH_POLL_REVIEW_MS, LOCKING, STATUS_META, FOLD, FOLD_META, DISMISSABLE, batchPollDelay, batchCounts, safeHttpUrl,
  formatWhen, plural, newestFirst, partitionBatches, foldReason, queueSummary, foldedSummary, FOCUS, ERROR_BOX,
  EXTERNAL_LINK, errorText, isDisabledResponse, readStoredFlag, writeStoredFlag,
} from './autoResolveBatchShared';

// "Batch runs" (docs/auto-resolve-batch.md §3, EXPERIMENTAL): every school's
// batches above the Edit Requests table, whatever the list's filters.
//
// Attention first: the list shows only the batches that still want something
// — working, waiting for review, needing a person or failed (unless dismissed
// or superseded; partitionBatches). Everything else (published, dismissed,
// superseded, discarded) folds into ONE summary row at the bottom with
// Show all. The header counts what is waiting ("2 running · 3 to review").
// Each row shows the school, when it started, its status and counts, links to
// the agent run and the PR, and Dismiss (needs a person / failed) or Restore
// (dismissed); expanding it shows the same per-batch detail as the
// per-school panel (AutoResolveBatchCard), with its Merge & publish /
// Discard / Refresh going to that school's endpoint.
//
// Collapsed state is remembered (nexsav-batch-strip-collapsed). With nothing
// stored, the strip opens collapsed when nothing is waiting, expanded
// otherwise — decided at the first load, and again when the page reloads it
// after starting batches (so the new runs show); a stored choice always wins.
//
// Polls like the panel (6 s while an agent works, 12 s while a PR waits,
// stops when every batch is settled). Renders nothing while the UI flag is
// off or when the API answers 404 { code: 'disabled' } (the server switch);
// `onAvailability(on)` tells the page which, so its header button and bulk
// action follow the same switch.

export const BATCH_STRIP_ENDPOINT = '/api/admin/auto-resolve-batches?limit=20';
export const BATCH_STRIP_COLLAPSED_KEY = 'nexsav-batch-strip-collapsed';

const FALLBACK = {
  [AUTO_RESOLVE_BATCH_ACTIONS.MERGE]: 'Couldn’t publish this batch.',
  [AUTO_RESOLVE_BATCH_ACTIONS.DISCARD]: 'Couldn’t discard this batch.',
  [AUTO_RESOLVE_BATCH_ACTIONS.REFRESH]: 'Couldn’t refresh this batch.',
  [AUTO_RESOLVE_BATCH_ACTIONS.DISMISS]: 'Couldn’t dismiss this batch.',
  [AUTO_RESOLVE_BATCH_ACTIONS.RESTORE]: 'Couldn’t restore this batch.',
};

// Actions on the row itself (their errors show on the row, not in the card).
const ROW_ACTIONS = [AUTO_RESOLVE_BATCH_ACTIONS.DISMISS, AUTO_RESOLVE_BATCH_ACTIONS.RESTORE];

const MOVED_TEXT = {
  [FOLD.PUBLISHED]: 'was published',
  [FOLD.DISMISSED]: 'was dismissed',
  [FOLD.SUPERSEDED]: 'is superseded by a later batch',
  [FOLD.DISCARDED]: 'was discarded',
};

function accountEndpoint(accountId) {
  return `/api/admin/accounts/${encodeURIComponent(accountId || '')}/auto-resolve-batch`;
}

function normalizeBatches(res) {
  return Array.isArray(res?.batches) ? res.batches.filter((b) => b && b.id != null) : [];
}

function isActive(b) {
  return LOCKING.includes(b?.status);
}

// Active batches (still working, or waiting for review) first, newest first.
function stripOrder(a, b) {
  return (isActive(a) ? 0 : 1) - (isActive(b) ? 0 : 1) || newestFirst(a, b);
}

function schoolOf(b) {
  return b?.account?.name || 'Unknown school';
}

export default function AutoResolveBatchStrip({ reloadKey = 0, onAvailability, onBatchesChanged }) {
  const flagOn = Boolean(AUTO_RESOLVE_BATCH_ENABLED);
  const baseId = useId();

  // phase: loading (renders nothing) | ready | error | hidden (server switch off)
  const [phase, setPhase] = useState('loading');
  const [batches, setBatches] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [refreshError, setRefreshError] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(null); // { kind, batchId }
  const [actionError, setActionError] = useState(null); // { batchId, kind, message }
  // The admin's own choice (stored), else the default decided at the first load.
  const [chosenCollapsed, setChosenCollapsed] = useState(() => readStoredFlag(BATCH_STRIP_COLLAPSED_KEY, null));
  const [defaultCollapsed, setDefaultCollapsed] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [notice, setNotice] = useState('');

  const seqRef = useRef(0);
  const mountedRef = useRef(false);
  const hasDataRef = useRef(false);
  const statusesRef = useRef(new Map());
  // Where focus goes once a row moved between the queue and the folded list.
  const focusRef = useRef(null);
  // The page bumped reloadKey: the next load decides the default again.
  const lastReloadKeyRef = useRef(reloadKey);
  const redecideRef = useRef(false);
  // The page's callbacks, read at call time so a new function each render
  // never re-runs the loading effect.
  const availabilityRef = useRef(onAvailability);
  const changedRef = useRef(onBatchesChanged);
  availabilityRef.current = onAvailability;
  changedRef.current = onBatchesChanged;

  // → the loaded list, or null when this load failed or was overtaken.
  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    setUpdating(true);
    try {
      const res = await api.get(BATCH_STRIP_ENDPOINT);
      if (!mountedRef.current || seq !== seqRef.current) return null;
      const list = normalizeBatches(res);
      // A batch that moved on since the last load changed its requests too
      // (claimed, resolved, released): the page reloads its list.
      const before = statusesRef.current;
      const moved = hasDataRef.current && list.some((b) => before.has(String(b.id)) && before.get(String(b.id)) !== b.status);
      statusesRef.current = new Map(list.map((b) => [String(b.id), b.status]));
      hasDataRef.current = true;
      setBatches(list);
      setPhase('ready');
      setRefreshError(false);
      setLoadError('');
      const redecide = redecideRef.current;
      redecideRef.current = false;
      setDefaultCollapsed((d) => (d === null || redecide ? partitionBatches(list).queue.length === 0 : d));
      availabilityRef.current?.(true);
      if (moved) changedRef.current?.();
      return list;
    } catch (err) {
      if (!mountedRef.current || seq !== seqRef.current) return null;
      if (isDisabledResponse(err)) {
        setPhase('hidden');
        availabilityRef.current?.(false);
      } else if (hasDataRef.current) {
        setRefreshError(true);
      } else {
        setLoadError(errorText(err, 'Couldn’t load batch runs.'));
        setPhase('error');
        // Open, so the error and its retry show.
        setDefaultCollapsed((d) => (d === null ? false : d));
        availabilityRef.current?.(true);
      }
      return null;
    } finally {
      if (mountedRef.current && seq === seqRef.current) {
        setUpdating(false);
        setTick((t) => t + 1);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // First load, and again whenever the page asks (it just started batches).
  useEffect(() => {
    if (!flagOn) return;
    if (lastReloadKeyRef.current !== reloadKey) {
      lastReloadKeyRef.current = reloadKey;
      redecideRef.current = true;
    }
    load();
  }, [flagOn, load, reloadKey]);

  const ordered = useMemo(() => [...batches].sort(stripOrder), [batches]);
  const { queue, folded, counts } = useMemo(() => partitionBatches(ordered), [ordered]);

  // Self-scheduling poll: re-armed after every load (tick), stops once every
  // batch is settled. A failed refresh retries at the slow pace.
  const pollDelay = phase === 'ready'
    ? (batchPollDelay(batches) || (refreshError ? BATCH_POLL_REVIEW_MS : 0))
    : 0;
  useEffect(() => {
    if (!pollDelay) return undefined;
    const t = setTimeout(load, pollDelay);
    return () => clearTimeout(t);
  }, [pollDelay, tick, load]);

  const foldToggleId = `${baseId}-folded-toggle`;
  const rowToggleId = (id) => `${baseId}-toggle-${id}`;

  // A row that left the queue (or came back) takes focus with it: onto its
  // own toggle when it is still on screen, else onto Show all / Show less.
  useEffect(() => {
    const ids = focusRef.current;
    if (!ids) return;
    focusRef.current = null;
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) { el.focus(); break; }
    }
  });

  // Every action goes to the batch's own school endpoint.
  const runAction = async (batch, kind) => {
    if (busy || !batch?.account?.id) return false;
    seqRef.current += 1; // a poll already in flight must not undo what this action shows
    setBusy({ kind, batchId: batch.id });
    setActionError(null);
    const wasFolded = foldReason(batch);
    // The batch moved between the queue and the folded list: say so, and
    // take focus along.
    const settle = (b) => {
      if (!b) return false;
      const nowFolded = foldReason(b);
      if (Boolean(nowFolded) === Boolean(wasFolded)) return false;
      const school = schoolOf(batch);
      setNotice(nowFolded
        ? `${school}’s batch ${MOVED_TEXT[nowFolded] || 'was folded'} and moved under Show all.`
        : `${school}’s batch is back in the list.`);
      focusRef.current = nowFolded && !showAll ? [foldToggleId] : [rowToggleId(batch.id), foldToggleId];
      return true;
    };
    let ok = false;
    let settled = false;
    try {
      const res = await api.post(accountEndpoint(batch.account.id), { action: kind, batchId: batch.id });
      const b = res?.batch && typeof res.batch === 'object' ? res.batch : null;
      if (b && b.id != null) {
        setBatches((prev) => prev.map((x) => (String(x.id) === String(b.id) ? { ...x, ...b, account: x.account } : x)));
        settled = settle({ ...batch, ...b });
      }
      ok = true;
    } catch (err) {
      setActionError({ batchId: batch.id, kind, message: errorText(err, FALLBACK[kind] || 'Something went wrong.') });
    } finally {
      setBusy(null);
      const list = await load();
      if (ok && !settled && list) settle(list.find((x) => String(x.id) === String(batch.id)));
    }
    return ok;
  };

  const collapsed = chosenCollapsed ?? defaultCollapsed ?? false;

  const toggleCollapsed = () => {
    const next = !collapsed;
    setChosenCollapsed(next);
    writeStoredFlag(BATCH_STRIP_COLLAPSED_KEY, next);
  };

  const toggleExpanded = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(String(id))) next.delete(String(id)); else next.add(String(id));
    return next;
  });

  if (!flagOn || phase === 'loading' || phase === 'hidden') return null;

  const titleId = `${baseId}-title`;
  const bodyId = `${baseId}-body`;
  const foldedListId = `${baseId}-folded`;
  const waiting = queueSummary(counts);
  const foldedText = foldedSummary(counts);
  const countLine = phase === 'ready'
    ? (batches.length ? (waiting || 'Nothing waiting') : 'No batch runs yet')
    : 'Couldn’t load';

  const newest = [...batches].sort(newestFirst)[0];
  const newestMeta = newest ? STATUS_META[newest.status] : null;
  const liveText = newest && newestMeta
    ? `Newest batch run (${schoolOf(newest)}, ${plural(batchCounts(newest).total, 'request')}) ${newestMeta.sentence}.`
    : '';

  const renderRow = (b, reason) => {
    const mine = actionError && String(actionError.batchId) === String(b.id) ? actionError : null;
    return (
      <BatchRunRow
        key={b.id}
        batch={b}
        reason={reason}
        baseId={baseId}
        toggleId={rowToggleId(b.id)}
        open={expanded.has(String(b.id))}
        onToggle={() => toggleExpanded(b.id)}
        busyKind={busy && String(busy.batchId) === String(b.id) ? busy.kind : null}
        anyBusy={Boolean(busy)}
        error={mine && !ROW_ACTIONS.includes(mine.kind) ? mine.message : ''}
        rowError={mine && ROW_ACTIONS.includes(mine.kind) ? mine.message : ''}
        onAction={(kind) => runAction(b, kind)}
      />
    );
  };

  return (
    <section
      aria-labelledby={titleId}
      className="mb-4 rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden"
      data-testid="batch-runs-strip"
    >
      <div className="flex flex-wrap items-center gap-3 px-4 md:px-5 py-3">
        <div className="min-w-0 flex-1">
          <h2 id={titleId} className="text-[14px] font-semibold text-white leading-tight">
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-expanded={!collapsed}
              aria-controls={bodyId}
              className={`flex items-center gap-2.5 text-left rounded-lg ${FOCUS}`}
            >
              {collapsed
                ? <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" aria-hidden="true" />
                : <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" aria-hidden="true" />}
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-xl bg-[#B0FC38]/15 border border-[#B0FC38]/40 shrink-0" aria-hidden="true">
                <Layers className="w-3.5 h-3.5 text-[#B0FC38]" />
              </span>
              <span>Batch runs</span>
              <span className="px-1.5 py-0.5 rounded-full bg-[#B0FC38]/10 border border-white/10 text-[9px] font-semibold uppercase tracking-wide text-[#B0FC38]">Experimental</span>
            </button>
          </h2>
          <p className="mt-1 text-[12px] text-gray-400 leading-snug" data-testid="batch-runs-subtitle">
            <span data-testid="batch-runs-counts">{countLine}</span>
            {collapsed && phase === 'ready' && foldedText && (
              <span data-testid="batch-runs-folded-counts">
                <span aria-hidden="true"> · </span><span className="sr-only">; folded: </span>{foldedText}
              </span>
            )}
          </p>
        </div>
        {updating && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500" data-testid="batch-runs-updating">
            <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> Updating…
          </span>
        )}
      </div>

      {/* Status region: the newest run's state for screen readers, and the
          visible refresh-error line. */}
      <div role="status" aria-live="polite" aria-atomic="true" data-testid="batch-runs-status">
        <span className="sr-only">{liveText}</span>
        {refreshError && (
          <p className="px-4 md:px-5 pb-2 text-[11px] text-amber-300/70" data-testid="batch-runs-refresh-error">
            ⚠ Couldn’t refresh, retrying…
          </p>
        )}
      </div>
      {/* What an action just did to the list (a row folded away or came back). */}
      <div role="status" aria-live="polite" className="sr-only" data-testid="batch-runs-notice">{notice}</div>

      {!collapsed && (
        <div id={bodyId} className="border-t border-white/[0.06] px-4 md:px-5 py-3">
          {phase === 'error' && (
            <div className={`${ERROR_BOX} flex flex-wrap items-center justify-between gap-2`} role="alert">
              <span>{loadError}</span>
              <button type="button" onClick={load} className={`underline underline-offset-2 hover:text-white rounded ${FOCUS}`}>Try again</button>
            </div>
          )}
          {phase === 'ready' && ordered.length === 0 && (
            <p className="text-[12px] text-gray-500" data-testid="batch-runs-empty">
              No batch runs yet. Select requests below and choose Auto-resolve selected, or use Batch auto-resolve above.
            </p>
          )}
          {phase === 'ready' && ordered.length > 0 && queue.length === 0 && (
            <p className="text-[12px] text-gray-400" data-testid="batch-runs-queue-empty">
              Nothing waiting. Finished batch runs are folded below.
            </p>
          )}
          {phase === 'ready' && queue.length > 0 && (
            <ol aria-label="Batch runs waiting" className="rounded-xl border border-white/[0.06] divide-y divide-white/[0.04]" data-testid="batch-runs-queue">
              {queue.map((b) => renderRow(b, null))}
            </ol>
          )}
          {phase === 'ready' && folded.length > 0 && (
            <>
              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 px-3 text-[11px] text-gray-400" data-testid="batch-runs-folded-summary">
                <span>{foldedText}</span>
                <span aria-hidden="true">·</span>
                <button
                  id={foldToggleId}
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  aria-expanded={showAll}
                  aria-controls={foldedListId}
                  className={`underline underline-offset-2 text-gray-300 hover:text-white rounded ${FOCUS}`}
                >
                  {showAll ? 'Show less' : 'Show all'}
                  <span className="sr-only"> ({plural(folded.length, 'folded batch run')})</span>
                </button>
              </div>
              {showAll && (
                <ol
                  id={foldedListId}
                  aria-label="Folded batch runs"
                  className="mt-2 rounded-xl border border-white/[0.06] divide-y divide-white/[0.04]"
                  data-testid="batch-runs-folded"
                >
                  {folded.map((b) => renderRow(b, foldReason(b)))}
                </ol>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

function BatchRunRow({ batch, reason, baseId, toggleId, open, onToggle, busyKind, anyBusy, error, rowError, onAction }) {
  const school = schoolOf(batch);
  const counts = batchCounts(batch);
  const runHref = safeHttpUrl(batch.run_url);
  const prHref = safeHttpUrl(batch.pr_url);
  const started = formatWhen(batch.created_at);
  const detailId = `${baseId}-run-${batch.id}`;
  const canDismiss = !reason && DISMISSABLE.includes(batch.status);
  const canRestore = reason === FOLD.DISMISSED;
  const rowAction = canRestore ? AUTO_RESOLVE_BATCH_ACTIONS.RESTORE : AUTO_RESOLVE_BATCH_ACTIONS.DISMISS;
  return (
    <li data-testid={`batch-run-${batch.id}`} data-status={batch.status} data-fold={reason || undefined}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5">
        <button
          id={toggleId}
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={detailId}
          aria-label={`${school}: batch of ${plural(counts.total, 'request')}${started ? `, started ${started}` : ''}`}
          className={`flex items-center gap-2 min-w-0 text-left rounded-lg ${FOCUS}`}
        >
          {open
            ? <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" aria-hidden="true" />
            : <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" aria-hidden="true" />}
          <span className="text-[13px] font-medium text-white break-words">{school}</span>
        </button>
        {started && <span className="text-[11px] text-gray-500">Started {started}</span>}
        <BatchStatusChip status={batch.status} testId="batch-run-status" />
        {reason && FOLD_META[reason]?.reasonChip && <FoldReasonChip reason={reason} testId="batch-run-fold-reason" />}
        <span className="text-[11px] text-gray-400" data-testid="batch-run-counts">
          {plural(counts.total, 'request')} · {counts.done} done · {counts.needsPerson} {counts.needsPerson === 1 ? 'needs' : 'need'} a person · {counts.left} left
        </span>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] ml-auto">
          {runHref && (
            <a href={runHref} target="_blank" rel="noopener noreferrer" className={EXTERNAL_LINK}>
              Agent run
              <ExternalLink className="w-3 h-3" aria-hidden="true" /><span className="sr-only">for {school} (opens in a new tab)</span>
            </a>
          )}
          {prHref && (
            <a href={prHref} target="_blank" rel="noopener noreferrer" className={EXTERNAL_LINK}>
              {batch.pr_number ? `Change #${batch.pr_number}` : 'Pull request'}
              <ExternalLink className="w-3 h-3" aria-hidden="true" /><span className="sr-only">for {school} on GitHub (opens in a new tab)</span>
            </a>
          )}
          {(canDismiss || canRestore) && (
            <DismissButton
              restore={canRestore}
              school={school}
              busy={busyKind === rowAction}
              disabled={anyBusy}
              onClick={() => onAction(rowAction)}
            />
          )}
        </span>
      </div>
      {rowError && <div className={`${ERROR_BOX} mx-3 mb-2.5`} role="alert">{rowError}</div>}
      {open && (
        <div id={detailId} className="px-3 pb-3">
          <BatchCard
            as="div"
            headingLevel={3}
            batch={batch}
            school={school}
            busyKind={busyKind}
            anyBusy={anyBusy}
            error={error}
            onAction={onAction}
          />
        </div>
      )}
    </li>
  );
}
